import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useLang } from "../../i18n";
import { resolveMedia } from "../../lib/api";
import { Reveal } from "../Reveal";

const IMG_WOMEN = "/api/files/anansha-welfare/uploads/fb563bd0-0b02-4fa0-b516-895a58656731.jpeg";
const IMG_EDU = "/api/files/anansha-welfare/uploads/be3b382d-38a0-4a6e-b236-26035f7212f1.jpeg";
const IMG_ENV = "/api/files/anansha-welfare/uploads/223fd557-6ee4-4f2d-8ae8-3d8eeed5ea53.jpeg";
const IMG_HEALTH = "/api/files/anansha-welfare/uploads/images/gallery/events/0795cbb8-fbaa-43e8-9abd-a936ddb584c4.jpg";

function Split({ testid, img, imgAlt, overline, title, desc, points, cta, ctaTo, flip = false, tint = "bg-white" }) {
  const { pick } = useLang();
  return (
    <section data-testid={testid} className={`py-24 ${tint}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal className={`relative ${flip ? "lg:order-2" : ""}`}>
          <div className="relative rounded-[2rem] overflow-hidden shadow-[0_24px_70px_rgba(23,107,58,0.16)]">
            <img src={resolveMedia(img)} alt={imgAlt} loading="lazy" className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-ngo-forest/40 to-transparent" />
          </div>
        </Reveal>
        <div className={flip ? "lg:order-1" : ""}>
          <Reveal>
            <p className="text-xs sm:text-sm font-semibold tracking-[0.25em] uppercase text-ngo-leaf mb-4">{pick(overline)}</p>
          </Reveal>
          <Reveal i={1}>
            <h2 data-testid={`${testid}-heading`} className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight text-ngo-ink">{pick(title)}</h2>
          </Reveal>
          <Reveal i={2}>
            <p className="mt-5 text-base md:text-lg leading-relaxed text-ngo-ink/70">{pick(desc)}</p>
          </Reveal>
          {points && (
            <ul className="mt-7 grid sm:grid-cols-2 gap-3">
              {points.map((p, i) => (
                <Reveal key={i} i={i}>
                  <li className="flex items-center gap-2.5 text-sm font-medium text-ngo-forest bg-ngo-mint rounded-xl px-4 py-3">
                    <CheckCircle2 size={16} className="shrink-0 text-ngo-gold" />
                    {pick(p)}
                  </li>
                </Reveal>
              ))}
            </ul>
          )}
          {cta && (
            <Reveal i={3}>
              <Link
                to={ctaTo}
                data-testid={`${testid}-cta`}
                className="group mt-8 inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-ngo-deep text-white font-semibold hover:bg-ngo-forest hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
              >
                {pick(cta)}
                <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}

export function WomenEmpowerment() {
  return (
    <Split
      testid="women-section"
      tint="bg-ngo-cream"
      img={IMG_WOMEN}
      imgAlt="महिला सशक्तिकरण प्रशिक्षण"
      overline={{ hi: "महिला सशक्तिकरण", en: "Women Empowerment" }}
      title={{ hi: "महिला सशक्तिकरण – आत्मनिर्भरता की ओर एक कदम", en: "Women Empowerment – A Step Towards Self-Reliance" }}
      desc={{
        hi: "महिलाओं को आत्मनिर्भर बनाने के लिए अंशा वेलफेयर सोसाइटी द्वारा कौशल विकास एवं प्रशिक्षण गतिविधियों को बढ़ावा दिया जा रहा है। सिलाई प्रशिक्षण जैसे कार्यक्रम महिलाओं को अपने कौशल को विकसित करने और आय के अवसरों की दिशा में आगे बढ़ने में सहायता करते हैं।",
        en: "Anansha Welfare Society promotes skill development and training activities to make women self-reliant. Programs like sewing training help women develop their skills and move towards income opportunities.",
      }}
      cta={{ hi: "हमारी महिला सशक्तिकरण पहल देखें", en: "See Our Women Empowerment Initiative" }}
      ctaTo="/our-work"
    />
  );
}

export function Healthcare() {
  return (
    <Split
      flip
      testid="health-section"
      tint="bg-white"
      img={IMG_HEALTH}
      imgAlt="स्वास्थ्य जांच शिविर — खेमापुर"
      overline={{ hi: "स्वास्थ्य सेवा", en: "Healthcare" }}
      title={{ hi: "स्वास्थ्य सेवा – स्वस्थ समाज की ओर", en: "Healthcare – Towards a Healthy Society" }}
      desc={{
        hi: "स्वस्थ समाज के निर्माण के लिए नियमित स्वास्थ्य जागरूकता एवं स्वास्थ्य जांच गतिविधियों के माध्यम से जरूरतमंद समुदायों तक पहुंचने का प्रयास किया जाता है।",
        en: "Through regular health awareness and health check-up activities, we strive to reach communities in need to build a healthy society.",
      }}
      points={[
        { hi: "स्वास्थ्य जांच", en: "Health check-ups" },
        { hi: "स्वास्थ्य जागरूकता", en: "Health awareness" },
        { hi: "प्राथमिक स्वास्थ्य जानकारी", en: "Primary health information" },
        { hi: "ग्रामीण समुदाय तक पहुंच", en: "Reaching rural communities" },
        { hi: "जरूरतमंद लोगों का सहयोग", en: "Supporting people in need" },
      ]}
    />
  );
}

export function Education() {
  return (
    <Split
      testid="education-section"
      tint="bg-ngo-cream"
      img={IMG_EDU}
      imgAlt="बच्चों के साथ शिक्षा गतिविधि"
      overline={{ hi: "शिक्षा एवं बाल कल्याण", en: "Education & Child Welfare" }}
      title={{ hi: "शिक्षा और बाल कल्याण", en: "Education & Child Welfare" }}
      desc={{
        hi: "हर बच्चे को सीखने, आगे बढ़ने और अपने सपनों को पूरा करने का अवसर मिलना चाहिए। संस्था बच्चों के बीच शिक्षा, जागरूकता और उपयोगी सामग्री से जुड़ी गतिविधियों के माध्यम से सकारात्मक वातावरण बनाने का प्रयास करती है।",
        en: "Every child deserves the opportunity to learn, grow and fulfil their dreams. The organization works to create a positive environment through education, awareness and learning-material activities for children.",
      }}
      points={[
        { hi: "शिक्षा जागरूकता", en: "Education awareness" },
        { hi: "अध्ययन सामग्री सहयोग", en: "Study material support" },
        { hi: "बच्चों के साथ संवाद", en: "Dialogue with children" },
        { hi: "रचनात्मक गतिविधियां", en: "Creative activities" },
        { hi: "सामाजिक जागरूकता", en: "Social awareness" },
      ]}
    />
  );
}

export function Environment() {
  return (
    <Split
      flip
      testid="environment-section"
      tint="bg-ngo-mint/60"
      img={IMG_ENV}
      imgAlt="वृक्षारोपण अभियान"
      overline={{ hi: "पर्यावरण संरक्षण", en: "Environment Protection" }}
      title={{ hi: "पर्यावरण संरक्षण – आज एक पौधा, कल एक बेहतर भविष्य", en: "Environment – One Sapling Today, a Better Tomorrow" }}
      desc={{
        hi: "वृक्ष हमारे जीवन का आधार हैं। अंशा वेलफेयर सोसाइटी पर्यावरण संरक्षण और हरियाली को बढ़ावा देने के लिए वृक्षारोपण एवं जागरूकता गतिविधियों में समुदाय की भागीदारी को प्रोत्साहित करती है।",
        en: "Trees are the foundation of our lives. Anansha Welfare Society encourages community participation in tree plantation and awareness activities to promote environment protection and greenery.",
      }}
      cta={{ hi: "पर्यावरण अभियान से जुड़ें", en: "Join the Environment Campaign" }}
      ctaTo="/volunteer"
    />
  );
}
